/**
 * Helpers for the company's org taxonomy — the departments and designations
 * an employee can carry. The lists themselves are managed by admins in Admin
 * Settings and loaded with useOrgOptions() (utils/orgOptions.js); the server
 * coerces anything off-list to "Not assigned" (server/utils/orgTaxonomy.js).
 */

export const NOT_ASSIGNED = 'Not assigned';

/**
 * Orders department group headings on the boards that group by department.
 * Pass the admin's department list (useOrgOptions().departments) and use the
 * result as a sort comparator.
 *
 * Departments read in the order HR set, not alphabetically, and
 * "Not assigned" sorts last — it is a to-do list for HR, not a department, and
 * it has no business sitting between two real ones. Anything off the list
 * (a record written before this taxonomy existed) falls in after the real
 * departments and before "Not assigned", alphabetically among its own kind.
 */
export const byDepartment = (departments = []) => {
    const rank = (d) => {
        if (d === NOT_ASSIGNED) return departments.length + 1;
        const i = departments.indexOf(d);
        return i === -1 ? departments.length : i;
    };
    return (a, b) => rank(a) - rank(b) || a.localeCompare(b);
};

// Blank/null joins the "Not assigned" bucket rather than forming a second,
// differently-named one beside it. The trim guards records written before the
// taxonomy landed, when a trailing space made its own department.
export const normDept = (d) => (d || '').trim() || NOT_ASSIGNED;

/**
 * What to *display* for a department or designation.
 *
 * "Not assigned" is the right thing to store — it makes "nobody has picked
 * yet" one named bucket instead of a mix of "", null and undefined — but it is
 * the wrong thing to print in a chat list or under an avatar, where the old
 * blank value fell through to the person's role ("Team Lead", "HR"). Returns
 * an empty string for it so every `orgLabel(x) || fallback` and
 * `.filter(Boolean)` reads exactly as it did when the field was blank.
 *
 * Screens whose whole job is to show the field — the employee profile, the
 * edit form, the workload board's group headings — print the stored value
 * instead, because there "Not assigned" is the answer.
 */
export const orgLabel = (value) => {
    const v = (value || '').trim();
    return v === NOT_ASSIGNED ? '' : v;
};

/**
 * The <option> values for one list, with "Not assigned" first.
 *
 * A stored value that is not on the list (a record written before this
 * taxonomy existed) is appended as its own option so the select still shows
 * what is actually stored instead of silently displaying the first entry and
 * rewriting the record on save.
 */
export const taxonomyOptions = (values, current) => {
    const list = [...values];
    const stored = (current || '').trim();
    if (stored && !list.includes(stored)) list.push(stored);
    return list;
};
